import type React from 'react';
import type { TourStep } from './Tour';
import type {
  TourStepTooltipBalloonConfig,
  TourStepTooltipConfig,
} from './TourStepTooltip';

export type EditToolCornerRadiusKeys =
  | 'leftUp'
  | 'rightUp'
  | 'leftDown'
  | 'rightDown';

export interface EditToolCornerRadiusValues {
  leftUp: number;
  rightUp: number;
  leftDown: number;
  rightDown: number;
}

export interface EditToolBoxEdgeValues {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export interface EditToolColorValues {
  r: number;
  g: number;
  b: number;
  a: number;
}

export interface EditToolSpotlightOptionsValues {
  isActive: boolean;
  color: EditToolColorValues;
}

export interface EditToolColorAndShape {
  backgroundColor: EditToolColorValues;
  borderRadius: EditToolCornerRadiusValues;
  padding: EditToolBoxEdgeValues;
}

export interface EditToolBoxShadowValues {
  isActive: boolean;
  boxShadowColor: EditToolColorValues;
  boxShadowParams: EditToolBoxShadowParams;
}

export type EditToolBoxShadowKeys = 'blur' | 'spread' | 'x' | 'y';

export interface EditToolBoxShadowParams {
  blur: number;
  spread: number;
  x: number;
  y: number;
}

export type EditToolFontFamily =
  | 'Inter, sans-serif'
  | 'Arial, sans-serif'
  | 'Verdana, sans-serif'
  | 'Helvetica, sans-serif'
  | 'Tahoma, sans-serif'
  | 'Trebuchet, sans-serif'
  | 'Times New Roman, serif'
  | 'Georgia, serif'
  | 'Garamond, serif'
  | 'Courier New, monospaced'
  | 'Brush Script MT, cursive';

export interface EditToolFontValues {
  fontSize: number;
  fontFamily: EditToolFontFamily | string;
}

export type AlignTextType = 'textLeft' | 'textCenter' | 'textRight';

export type EditToolTextFormatValues = {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  alignText: AlignTextType;
};

export interface EditToolCheckboxItem {
  name: string;
  checked: boolean;
}

export interface EditToolCheckboxConfigItem {
  label: string;
  name: string;
}

export interface EditToolFontTypeValues {
  fontFamily: EditToolFontFamily | string;
  fontSize: number;
}
export interface EditToolTextStyle {
  fontType: EditToolFontTypeValues;
  textColor: EditToolColorValues;
  textFormat: EditToolTextFormatValues;
}

export enum ButtonActionEnums {
  nextStep = 'nextStep',
  previousStep = 'previousStep',
  startOverTour = 'startOverTour',
  externalUrl = 'externalUrl',
  customStep = 'customStep',
}

export type ButtonAction = keyof typeof ButtonActionEnums;

export interface EditToolButtonAction {
  name: ButtonAction;
  url?: string;
  goToStepId?: TourStep['stepId'] | null;
}

export interface EditToolNavigationOptionsValues {
  isButtonsNavigation: boolean;
  isElementNavigation: boolean;
  elementAction: EditToolButtonAction;
}

export interface EditToolHighlightElementOptionsValues {
  isActive: boolean;
  color: EditToolColorValues;
}

export enum RelativePositionTypeEnums {
  element = 'element',
  page = 'page',
}

export type RelativePositionType = RelativePositionTypeEnums;

export type RelativePositionAlignment =
  | 'topStart'
  | 'topCenter'
  | 'topEnd'
  | 'middleStart'
  | 'middleCenter'
  | 'middleEnd'
  | 'bottomStart'
  | 'bottomCenter'
  | 'bottomEnd';

export enum RelativePositionAlignmentEnums {
  topStart = 'topStart',
  topCenter = 'topCenter',
  topEnd = 'topEnd',
  middleStart = 'middleStart',
  middleCenter = 'middleCenter',
  middleEnd = 'middleEnd',
  bottomStart = 'bottomStart',
  bottomCenter = 'bottomCenter',
  bottomEnd = 'bottomEnd',
}

export type EditToolLayoutValues = {
  relation: RelativePositionType;
  alignment: RelativePositionAlignment;
};

export interface EditToolRelativePositionTypeProps {
  onChange: (values: TourStepTooltipBalloonConfig) => void;
  values: TourStepTooltipConfig;
  title?: string;
  onClick: () => void;
  attachedElement?: React.ReactElement;
  anchorRef: React.RefObject<any>;
  targetElement: HTMLElement | undefined;
}
